import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CountUp from 'react-countup';

import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

import { useAuth } from '../AuthContext';
import apiUrl from '../config';
import LayoutDashboard from '../layout/LayoutDashboard';
import '../CSS/Home.css';

const Home = () => {
  const navigate = useNavigate();
  const { role, isAuthenticated } = useAuth();

  const roleUpper = String(role || '').toUpperCase();
  const isAdmin = roleUpper === 'ADMIN';

  const [loading, setLoading] = useState(true);

  const [adminStats, setAdminStats] = useState({
    ressources: 0,
    declarations: 0,
    paie: 0,
  });

  const [userStats, setUserStats] = useState({
    declarations: 0,
    documents: 0,
    paie: 0,
  });

  useEffect(() => {
    let isMounted = true;

    const token = localStorage.getItem('token');

    if (!token) {
      navigate('/login');
      return;
    }

    const headers = { Authorization: `Bearer ${token}` };

    const loadStats = async () => {
      try {
        setLoading(true);

        if (isAdmin) {
          const response = await axios.get(`${apiUrl}/stats-summary`, { headers });

          if (!isMounted) return;

          setAdminStats({
            ressources: response.data?.user_count || 0,
            declarations: response.data?.declaration_count || 0,
            paie: response.data?.paie_count || 0,
          });
        } else {
          const response = await axios.get(`${apiUrl}/stats-user`, { headers });

          if (!isMounted) return;

          setUserStats({
            declarations: response.data?.mes_declarations || 0,
            documents: response.data?.mes_documents || 0,
            paie: response.data?.mes_paies || 0,
          });
        }
      } catch (error) {
        console.error('Erreur chargement accueil:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (isAuthenticated) {
      loadStats();
    }

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, isAdmin, navigate]);

  const openDeclarations = () => {
    if (isAdmin) {
      navigate('/ListeDeclarations');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const decoded = JSON.parse(atob(token.split('.')[1]));
      const userId = decoded.user_id;

      localStorage.setItem('selectedUserId', userId);
      navigate('/Déclarations', { state: { selectedUserId: userId } });
    } catch {
      navigate('/Déclarations');
    }
  };

  const statsCards = useMemo(() => {
    if (isAdmin) {
      return [
        {
          label: 'Ressources',
          value: adminStats.ressources,
          helper: 'comptes utilisateurs actifs',
          icon: <GroupsRoundedIcon />,
        },
        {
          label: 'Déclarations',
          value: adminStats.declarations,
          helper: 'déclarations enregistrées',
          icon: <AssignmentTurnedInRoundedIcon />,
        },
        {
          label: 'Fiches de paie',
          value: adminStats.paie,
          helper: 'documents de paie disponibles',
          icon: <PaymentsRoundedIcon />,
        },
      ];
    }

    return [
      {
        label: 'Mes déclarations',
        value: userStats.declarations,
        helper: 'déclarations transmises',
        icon: <AssignmentTurnedInRoundedIcon />,
      },
      {
        label: 'Mes documents',
        value: userStats.documents,
        helper: 'documents disponibles',
        icon: <FolderRoundedIcon />,
      },
      {
        label: 'Mes fiches de paie',
        value: userStats.paie,
        helper: 'fiches disponibles',
        icon: <PaymentsRoundedIcon />,
      },
    ];
  }, [isAdmin, adminStats, userStats]);

  const quickActions = useMemo(() => {
    const actions = [
      {
        title: isAdmin ? 'Déclarations' : 'Faire ma déclaration',
        description: isAdmin
          ? 'Consultez les déclarations, contrôlez les statuts et suivez les éléments transmis.'
          : 'Saisissez vos jours de travail, congés ou arrêt maladie.',
        icon: <AssignmentTurnedInRoundedIcon />,
        action: openDeclarations,
      },
      {
        title: 'Fiches de paie',
        description: 'Retrouvez les fiches de paie disponibles et les documents liés à la rémunération.',
        icon: <PaymentsRoundedIcon />,
        action: () => navigate('/Paie'),
      },
      {
        title: 'Documents',
        description: 'Accédez aux documents administratifs déposés dans votre espace.',
        icon: <FolderRoundedIcon />,
        action: () => navigate('/Documents'),
      },
    ];

    if (isAdmin) {
      actions.unshift({
        title: 'Ressources',
        description: 'Administrez les utilisateurs, leurs informations, leurs accès et leurs rôles.',
        icon: <GroupsRoundedIcon />,
        action: () => navigate('/ressources'),
      });

      actions.push({
        title: 'Factures',
        description: 'Suivez les factures, les échéances, les statuts et les montants.',
        icon: <ReceiptLongRoundedIcon />,
        action: () => navigate('/FacturePage'),
      });
    }

    return actions;
  }, [isAdmin, navigate]);

  return (
    <LayoutDashboard>
      <main className="nova-home-page">
        <section className="nova-home-hero">
          <div className="nova-home-hero-content">
            <span className="nova-home-pill">Tableau de bord</span>

            <h1>Bienvenue sur votre espace NOVA</h1>

            <p>
              Retrouvez en un seul endroit vos déclarations, documents, fiches de paie
              et ressources administratives.
            </p>
          </div>
        </section>

        <section className="nova-home-stats-grid">
          {statsCards.map((item) => (
            <article className="nova-home-stat-card" key={item.label}>
              <div className="nova-home-stat-icon">{item.icon}</div>

              <span>{item.label}</span>

              <strong>
                {loading ? '...' : <CountUp start={0} end={Number(item.value || 0)} duration={1.4} />}
              </strong>

              <small>{item.helper}</small>
            </article>
          ))}
        </section>

        <section className="nova-home-actions-panel">
          <div className="nova-home-section-head">
            <span className="nova-home-pill">Accès rapides</span>
            <h2>Actions principales</h2>
            <p>Accédez rapidement aux espaces les plus utilisés de la plateforme.</p>
          </div>

          <div className="nova-home-actions-grid">
            {quickActions.map((item) => (
              <button
                type="button"
                className="nova-home-action-card"
                key={item.title}
                onClick={item.action}
              >
                <span className="nova-home-action-icon">{item.icon}</span>

                <strong>{item.title}</strong>

                <p>{item.description}</p>

                <span className="nova-home-action-link">
                  Ouvrir <ArrowForwardRoundedIcon />
                </span>
              </button>
            ))}
          </div>
        </section>
      </main>
    </LayoutDashboard>
  );
};

export default Home;
